import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Card, Button, Input, Select, Label, EmptyState } from "../components/ui-kit";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export default function LeadsPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", source: "", status: "new", interaction_notes: "", next_action: "" });

  const load = async () => setRows((await api.get("/leads")).data);
  useEffect(() => {
    load();
    // Initial fetch only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", form);
      toast.success("Lead saved");
      setOpen(false);
      setForm({ name: "", mobile: "", source: "", status: "new", interaction_notes: "", next_action: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader
        title="Leads & Interactions"
        subtitle="Capture new prospects and your last conversation"
        actions={
          <Button onClick={() => setOpen((s) => !s)} data-testid="new-lead-btn">
            <Plus className="w-4 h-4" /> New lead
          </Button>
        }
      />
      {open && (
        <Card className="mb-6">
          <h3 className="font-heading text-xl font-bold mb-4">New lead / interaction</h3>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Mobile</Label>
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
            </div>
            <div>
              <Label>Source</Label>
              <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Walk-in, Referral, Fair, …" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="qualified">Qualified</option>
                <option value="lost">Lost</option>
                <option value="converted">Converted</option>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>Interaction notes</Label>
              <Input value={form.interaction_notes} onChange={(e) => setForm({ ...form, interaction_notes: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Next action</Label>
              <Input value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} />
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="lead-submit">
                Save lead
              </Button>
            </div>
          </form>
        </Card>
      )}
      <Card className="p-0 overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-8">
            <EmptyState title="No leads yet" />
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
              <tr className="text-left text-[11px] uppercase tracking-widest text-[#5C6B5E]">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Mobile</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Notes</th>
                <th className="px-4 py-3">Next action</th>
                <th className="px-4 py-3">By</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[#E5E2DC] hover:bg-[#F2EDE4]/60">
                  <td className="px-4 py-3 font-semibold">{r.name}</td>
                  <td className="px-4 py-3">{r.mobile}</td>
                  <td className="px-4 py-3">{r.source}</td>
                  <td className="px-4 py-3">
                    <span className="text-[11px] uppercase tracking-wider px-2 py-1 rounded bg-[#DDA15E]/25 text-[#8B5A15]">{r.status}</span>
                  </td>
                  <td className="px-4 py-3 max-w-xs text-[#5C6B5E]">{r.interaction_notes}</td>
                  <td className="px-4 py-3 text-[#5C6B5E]">{r.next_action}</td>
                  <td className="px-4 py-3 text-xs text-[#5C6B5E]">{r.user_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
